/**
 * Financial Baseline History Component (FM-2)
 * Sprint 12 - Financial Module MVP
 * 
 * Shows immutable financial baseline versions with variance tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  GitCommit, TrendingUp, TrendingDown, Clock, User, CheckCircle,
  FileText, Loader2, ChevronDown, ChevronRight, Info, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// API client for financial module
const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiGet = async (url, params = {}) => {
  return axios.get(`${API_BASE}${url}`, { headers: getAuthHeaders(), params });
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const VarianceBadge = ({ delta, percentage }) => {
  if (delta === 0 || delta === undefined) return null;
  
  const isPositive = delta > 0;
  const color = isPositive ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50';
  const Icon = isPositive ? TrendingUp : TrendingDown;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {isPositive ? '+' : ''}{formatCurrency(delta)} ({percentage?.toFixed(1)}%)
    </span>
  );
};

const BaselineCard = ({ baseline, isExpanded, onToggle, onViewVariance, isLatest }) => {
  const triggerLabels = {
    scope_approval: 'Scope Approval',
    decision_approval: 'Decision Approval',
    manual: 'Manual'
  };

  const isV1 = baseline.version === 1;
  const isVNext = baseline.version > 1;

  return (
    <div className={`border rounded-lg overflow-hidden mb-3 ${
      isLatest ? 'ring-2 ring-blue-500 shadow-md' : ''
    } ${isV1 ? 'border-green-200' : isVNext ? 'border-purple-200' : ''}`}>
      <div 
        className={`p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
          isV1 ? 'bg-green-50' : isVNext ? 'bg-purple-50' : 'bg-white'
        }`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isV1 ? 'bg-green-100' : isVNext ? 'bg-purple-100' : 'bg-gray-100'
          }`}>
            <GitCommit className={`w-5 h-5 ${
              isV1 ? 'text-green-600' : isVNext ? 'text-purple-600' : 'text-gray-600'
            }`} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">
                {isV1 ? 'V1 (Original)' : `V${baseline.version}`} - {baseline.baseline_id}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded ${
                isV1 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
              }`}>
                {triggerLabels[baseline.trigger] || baseline.trigger}
              </span>
              {isVNext && (
                <span className="text-xs px-2 py-0.5 bg-purple-200 text-purple-800 rounded font-semibold">
                  vNext
                </span>
              )}
              {isLatest && (
                <span className="text-xs px-2 py-0.5 bg-blue-500 text-white rounded font-semibold">
                  Current
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 flex items-center gap-4 mt-1">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(baseline.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {baseline.created_by_name || 'System'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="font-bold text-gray-900">{formatCurrency(baseline.total_cost)}</div>
            {baseline.variance && (
              <VarianceBadge 
                delta={baseline.variance.delta_cost} 
                percentage={baseline.variance.variance_percentage} 
              />
            )}
          </div>
          {baseline.variance && onViewVariance && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewVariance(); }}
              className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
              data-testid={`view-variance-btn-${baseline.version}`}
            >
              View Variance
            </button>
          )}
          {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
        </div>
      </div>
      
      {isExpanded && (
        <div className="border-t bg-gray-50 p-4">
          {/* Link to variance tab */}
          {baseline.variance && onViewVariance && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-800 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>This baseline has variance from V{baseline.variance.from_version}</span>
              </div>
              <button
                onClick={onViewVariance}
                className="text-xs px-3 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700 font-medium"
              >
                View Details in Variance Tab
              </button>
            </div>
          )}
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">CAPEX</div>
              <div className="font-semibold">{formatCurrency(baseline.total_capex)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">OPEX</div>
              <div className="font-semibold">{formatCurrency(baseline.total_opex)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Labor</div>
              <div className="font-semibold">{formatCurrency(baseline.total_labor)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Non-Labor</div>
              <div className="font-semibold">{formatCurrency(baseline.total_non_labor)}</div>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">Annual Benefits</div>
              <div className="font-semibold text-green-600">{formatCurrency(baseline.total_financial_benefit)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">ROI</div>
              <div className="font-semibold" style={{ color: baseline.roi_percentage >= 0 ? '#16a34a' : '#dc2626' }}>
                {baseline.roi_percentage?.toFixed(1)}%
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Payback</div>
              <div className="font-semibold">
                {baseline.payback_months > 0 ? `${baseline.payback_months} months` : 'N/A'}
              </div>
            </div>
          </div>
          
          {/* Variance Breakdown (if exists) */}
          {baseline.variance && (
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Variance from V{baseline.version - 1}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">ΔCAPEX:</span>
                  <span className={baseline.variance.delta_capex >= 0 ? 'text-red-600' : 'text-green-600'}>
                    {' '}{baseline.variance.delta_capex >= 0 ? '+' : ''}{formatCurrency(baseline.variance.delta_capex)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">ΔOPEX:</span>
                  <span className={baseline.variance.delta_opex >= 0 ? 'text-red-600' : 'text-green-600'}>
                    {' '}{baseline.variance.delta_opex >= 0 ? '+' : ''}{formatCurrency(baseline.variance.delta_opex)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">ΔBenefits:</span>
                  <span className={baseline.variance.delta_benefits >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {' '}{baseline.variance.delta_benefits >= 0 ? '+' : ''}{formatCurrency(baseline.variance.delta_benefits)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Total Variance:</span>
                  <span className={baseline.variance.delta_cost >= 0 ? 'text-red-600' : 'text-green-600'}>
                    {' '}{baseline.variance.variance_percentage?.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Assumptions snapshot */}
          {baseline.assumptions_snapshot?.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Assumptions at Baseline</h4>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                {baseline.assumptions_snapshot.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}

          {/* Decision link */}
          {baseline.decision_id && (
            <div className="border-t pt-4 mt-4 flex items-center gap-2 text-sm text-purple-600">
              <FileText className="w-4 h-4" />
              <span>Linked to Decision ID: {baseline.decision_id}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FinancialBaselineHistory = ({ scopeId, onNavigateToVariance }) => {
  const [loading, setLoading] = useState(true);
  const [baselines, setBaselines] = useState([]);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [totalVersions, setTotalVersions] = useState(0);
  const [currentVersion, setCurrentVersion] = useState(0);

  const loadHistory = useCallback(async () => {
    if (!scopeId) return;
    
    setLoading(true);
    try {
      const res = await apiGet(`/financial/baseline/history?scopeId=${scopeId}`);
      setBaselines(res.data.baselines || []);
      setTotalVersions(res.data.total_versions || 0);
      setCurrentVersion(res.data.current_version || 0);
      
      // Auto-expand latest version
      if (res.data.current_version > 0) {
        setExpandedVersion(res.data.current_version);
      }
    } catch (err) {
      console.error('Failed to load baseline history:', err);
      if (err.response?.status !== 404) {
        toast.error('Failed to load baseline history');
      }
    } finally {
      setLoading(false);
    }
  }, [scopeId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!scopeId) {
    return (
      <div className="p-6 text-center text-gray-500">
        <GitCommit className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Select a scope to view baseline history</p>
      </div>
    );
  }

  if (baselines.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        <GitCommit className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">No Financial Baselines</p>
        <p className="text-sm mt-1">A baseline will be created when the scope is approved</p>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="financial-baseline-history">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <GitCommit className="w-5 h-5 text-green-600" />
          Financial Baseline History
        </h2>
        <span className="text-sm text-gray-500">
          {totalVersions} version{totalVersions !== 1 ? 's' : ''} • Current: V{currentVersion}
        </span>
      </div>
      
      <div className="space-y-3">
        {baselines.slice().reverse().map((baseline) => (
          <BaselineCard
            key={baseline.baseline_id}
            baseline={baseline}
            isExpanded={expandedVersion === baseline.version}
            isLatest={baseline.version === currentVersion}
            onToggle={() => setExpandedVersion(
              expandedVersion === baseline.version ? null : baseline.version
            )}
            onViewVariance={baseline.variance ? onNavigateToVariance : null}
          />
        ))}
      </div>
    </div>
  );
};

export default FinancialBaselineHistory;
